import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonLoading,
  IonModal,
  IonTitle,
  IonToast,
  IonToolbar,
  useIonAlert,
} from "@ionic/react";
import { useCallback, useContext, useMemo, useState } from "react";
import { maxResults, useQueryExec } from "../hooks/useQueryExec";
import EquipmentResults from "./EquipmentResults";
import QueryFilter from "./QueryFilter";
import {
  TranslationMessagesContext,
  TranslationsContext,
} from "../util/Translations";
import SearchFooter from "./SearchFooter";
import { SelectedEquip } from "../models/equipment/Equipment";
import useDownloadEquipmentInspections from "../hooks/equipment/useDownloadEquipmentInspections";
import { useEquipmentStore } from "../store/equipment";
import { useOfflineStore } from "../store/offline-mode";
import { useShallow } from "zustand/react/shallow";

interface EquipmentSearchProps {
  equipmentClick?: (arg0?: any) => void;
  modalProps?: {
    isOpen: boolean;
    onModalCancel: () => void;
    onModalConfirm: (equipmentId: string) => void; // get Equipment
  };
  woNumber?: string; // If in use on the work order page
  equipmentChange?: boolean;
  onToggle?: (cardData: SelectedEquip) => void;
  selectedEquipIds?: string[];
  isSelectable?: boolean;
  hideFilter?: boolean;
}

const EquipmentSearch: React.FC<EquipmentSearchProps> = (props) => {
  const { translations } = useContext(TranslationsContext);
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const [showLoader, setShowLoader] = useState(false);
  const { trigger } = useDownloadEquipmentInspections();
  const setDownloadedEquip = useEquipmentStore(
    (state) => state.setDownloadedEquip
  );
  const isOffline = useOfflineStore((state) => state.isOffline);

  const toggleSelectedEquipment = useEquipmentStore(
    (state) => state.toggleSelectedEquipment
  );
  const resetSelectedEquipment = useEquipmentStore(
    (state) => state.resetSelectedEquipment
  );
  const selectedEquip = useEquipmentStore(
    useShallow((state) => state.selectedEquip)
  );
  const [presentAlert] = useIonAlert();

  const isSelectable = isOffline;
  const selectedEquipIds = useMemo(
    () => selectedEquip.map((e) => e.id),
    [selectedEquip]
  );

  const {
    equipmentClick,
    modalProps,
    woNumber,
    equipmentChange,

    hideFilter,
  } = props;
  const {
    getQueryList,
    queryList,
    getFilterParams,
    filterParams,
    setParameter,
    showLoading,
    setShowLoading,
    showToast,
    setShowToast,
    queryRows,
    setQueryRows,
    executeSearch,
    errorState,
    totalCount,
    queryCriteria,
    getQueryCriteria,
    getNoOfFieldsToDisplay,
    searchYn,
    setSearchYn,
    queryName,
    setQueryName,
    criteriaParams,
    confirmSearch,
    execFilter,
    resultsFilter,
    filterRows,
    queryInfo,
  } = useQueryExec("Equipment", "Equipment", "equipment");

  const querySection = "QW--Equipment";

  const onCancel = () => {
    // Clear modal data on cancel
    setQueryName("");
    setQueryRows([]);
    modalProps?.onModalCancel();
  };

  const handleDownloadEquipForOfflineAccess = useCallback(async () => {
    if (!selectedEquipIds?.length) return;
    setShowLoader(true);
    await trigger(selectedEquipIds);
    setDownloadedEquip(selectedEquip);
    resetSelectedEquipment();
    setShowLoader(false);
    presentAlert({
      message:
        translatedMessages["MobOfflineWoDownloadComplete"]?.MessageText ||
        "Equipment Inspections downloaded for offline access.",
      buttons: ["OK"],
    });
  }, [
    presentAlert,
    resetSelectedEquipment,
    selectedEquip,
    selectedEquipIds,
    setDownloadedEquip,
    translatedMessages,
    trigger,
  ]);

  const buildSearchComponents = () => {
    return (
      <>
        <IonLoading
          isOpen={showLoading}
          onDidDismiss={() => setShowLoading(false)}
          duration={5000}
        />
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={
            translatedMessages["DisplayFirst"]?.MessageText.replace(
              "@1",
              maxResults
            )
              .replace(
                "@2",
                translations["lbl_category_equipment"] || "Equipment"
              )
              .replace("@3", totalCount) ||
            `Displaying the first ${maxResults} equipment of ${totalCount}.`
          }
          duration={2000}
          position="top"
        />
        <QueryFilter
          setShowLoading={setShowLoading}
          getQueryList={getQueryList}
          queryList={queryList}
          querySection={querySection}
          getFilterParams={getFilterParams}
          filterParams={filterParams}
          setParameter={setParameter}
          queryCriteria={queryCriteria}
          getQueryCriteria={getQueryCriteria}
          searchCallback={confirmSearch}
          queryInfo={queryInfo}
          triggerId="equipment-qw-popover-trigger"
        />
        <EquipmentResults
          searchYn={searchYn}
          setSearchYn={setSearchYn}
          queryName={queryName}
          queryParams={filterParams}
          criteria={criteriaParams}
          queryRows={queryRows}
          setQueryRows={setQueryRows}
          filterParams={filterParams}
          setParameter={setParameter}
          executeSearch={executeSearch}
          getNoOfFieldsToDisplay={getNoOfFieldsToDisplay}
          errorState={errorState}
          totalCount={totalCount}
          showLoading={showLoading}
          setShowLoading={setShowLoading}
          woNumber={woNumber}
          equipmentClick={equipmentClick}
          modalProps={modalProps}
          filterRows={filterRows}
          filterMethod={execFilter}
          filterString={resultsFilter}
          equipmentChange={equipmentChange}
          hideFilter={hideFilter}
          isSelectable={isOffline}
          onToggle={toggleSelectedEquipment}
          selectedEquipIds={selectedEquip.map((e) => e.id)}
        />
      </>
    );
  };
  if (!!modalProps) {
    return (
      <IonModal isOpen={modalProps.isOpen} onDidDismiss={onCancel}>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton onClick={onCancel}>
                {translations["lbl_btn_cancel"] || "Cancel"}
              </IonButton>
            </IonButtons>
            <IonTitle>
              {translations["lbl_category_equipment"] || "Equipment"}
            </IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>{buildSearchComponents()}</IonContent>
        <SearchFooter totalRows={totalCount} filterRows={filterRows} />
      </IonModal>
    );
  }
  return (
    <>
      <IonLoading
        isOpen={showLoader}
        onDidDismiss={() => setShowLoader(false)}
      />
      <IonContent style={{ flex: "auto" }}>
        {buildSearchComponents()}
      </IonContent>
      <SearchFooter totalRows={totalCount} filterRows={filterRows}>
        {isSelectable && (
          <IonToolbar>
            <IonButtons slot="end">
              <IonButton
                disabled={!selectedEquipIds?.length}
                onClick={handleDownloadEquipForOfflineAccess}
                fill="solid"
              >
                {translations["mob_lbl_download"] || "Download"}
              </IonButton>
            </IonButtons>
          </IonToolbar>
        )}
      </SearchFooter>
    </>
  );
};

export default EquipmentSearch;
